/**
 * AI Settings Page
 *
 * Configure AI providers, models, prompts, and tasks.
 */

import { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Table,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Tag,
  Tooltip,
  Card,
  Tabs,
  Empty,
  Badge,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  StarOutlined,
  StarFilled,
  CheckCircleOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  FileTextOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiApi, type AIProvider, type AIModel, type AIPrompt, type AITask, type ProviderKey, PROVIDER_NAMES } from '@api/ai';
import { useAuthStore } from '@store/authStore';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Provider configuration form
interface ProviderFormValues {
  provider_key: ProviderKey;
  api_key: string;
  api_endpoint?: string;
}

// Prompt form
interface PromptFormValues {
  name: string;
  description?: string;
  category?: string;
  system_message?: string;
  template: string;
}

export default function AISettingsPage() {
  const queryClient = useQueryClient();
  const { isSuperAdmin, hasPermission } = useAuthStore();

  // State
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null);
  const [testingProvider, setTestingProvider] = useState<ProviderKey | null>(null);
  const [providerForm] = Form.useForm<ProviderFormValues>();
  const [promptForm] = Form.useForm<PromptFormValues>();

  // Permission check
  const canManageAI = isSuperAdmin() || hasPermission('ai:write');

  // Queries
  const { data: providersData, isLoading: providersLoading } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: () => aiApi.listProviders(),
  });

  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ['ai-models'],
    queryFn: () => aiApi.listModels(),
  });

  const { data: promptsData, isLoading: promptsLoading } = useQuery({
    queryKey: ['ai-prompts'],
    queryFn: () => aiApi.listPrompts(),
  });

  const { data: tasksData, isLoading: tasksLoading } = useQuery({
    queryKey: ['ai-tasks'],
    queryFn: () => aiApi.listTasks(),
  });

  const providers = providersData?.items || [];
  const models = modelsData?.items || [];
  const prompts = promptsData?.items || [];
  const tasks = tasksData?.items || [];

  // Mutations
  const addProviderMutation = useMutation({
    mutationFn: (data: ProviderFormValues) => aiApi.addProvider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-providers'] });
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
      message.success('Provider configured successfully');
      setProviderModalOpen(false);
      providerForm.resetFields();
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.error || 'Failed to configure provider';
      message.error(errorMsg);
    },
  });

  const deleteProviderMutation = useMutation({
    mutationFn: (providerKey: ProviderKey) => aiApi.deleteProvider(providerKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-providers'] });
      message.success('Provider removed');
    },
    onError: () => message.error('Failed to remove provider'),
  });

  const toggleStarMutation = useMutation({
    mutationFn: (modelId: string) => aiApi.toggleModelStar(modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (modelId: string) => aiApi.setDefaultModel(modelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-models'] });
      message.success('Default model updated');
    },
  });

  const createPromptMutation = useMutation({
    mutationFn: (data: PromptFormValues) => aiApi.createPrompt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });
      message.success('Prompt created');
      setPromptModalOpen(false);
      promptForm.resetFields();
    },
    onError: () => message.error('Failed to create prompt'),
  });

  const updatePromptMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PromptFormValues> }) =>
      aiApi.updatePrompt(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });
      message.success('Prompt updated');
      setPromptModalOpen(false);
      setEditingPrompt(null);
      promptForm.resetFields();
    },
    onError: () => message.error('Failed to update prompt'),
  });

  const deletePromptMutation = useMutation({
    mutationFn: (id: string) => aiApi.deletePrompt(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-prompts'] });
      message.success('Prompt deleted');
    },
    onError: () => message.error('Failed to delete prompt'),
  });

  // Handlers
  const handleTestProvider = useCallback(async (providerKey: ProviderKey) => {
    setTestingProvider(providerKey);
    try {
      const result = await aiApi.testProvider(providerKey);
      if (result.success) {
        message.success(result.message || 'Connection successful');
      } else {
        message.error(result.error || 'Connection failed');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Connection test failed');
    } finally {
      setTestingProvider(null);
    }
  }, []);

  const handleOpenProviderModal = useCallback(() => {
    providerForm.resetFields();
    setProviderModalOpen(true);
  }, [providerForm]);

  const handleOpenPromptModal = useCallback((prompt?: AIPrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      promptForm.setFieldsValue({
        name: prompt.name,
        description: prompt.description || '',
        category: prompt.category,
        system_message: prompt.system_message || '',
        template: prompt.template,
      });
    } else {
      setEditingPrompt(null);
      promptForm.resetFields();
    }
    setPromptModalOpen(true);
  }, [promptForm]);

  const handleSaveProvider = useCallback(async () => {
    try {
      const values = await providerForm.validateFields();
      addProviderMutation.mutate(values);
    } catch {
      // validation errors
    }
  }, [providerForm, addProviderMutation]);

  const handleSavePrompt = useCallback(async () => {
    try {
      const values = await promptForm.validateFields();
      if (editingPrompt) {
        updatePromptMutation.mutate({ id: editingPrompt.id, data: values });
      } else {
        createPromptMutation.mutate(values);
      }
    } catch {
      // validation errors
    }
  }, [promptForm, editingPrompt, createPromptMutation, updatePromptMutation]);

  // Table columns
  const providerColumns: ColumnsType<AIProvider> = [
    {
      title: 'Provider',
      dataIndex: 'provider_key',
      key: 'provider_key',
      render: (key: ProviderKey) => (
        <Space>
          <ApiOutlined />
          <Text strong>{PROVIDER_NAMES[key] || key}</Text>
        </Space>
      ),
    },
    {
      title: 'Endpoint',
      dataIndex: 'api_endpoint',
      key: 'api_endpoint',
      render: (endpoint?: string) => endpoint ? <Text code>{endpoint}</Text> : <Text type="secondary">Default</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: number) => (
        active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: AIProvider) => (
        <Space>
          <Tooltip title="Test connection">
            <Button
              type="text"
              size="small"
              icon={testingProvider === record.provider_key ? <LoadingOutlined /> : <ThunderboltOutlined />}
              onClick={() => handleTestProvider(record.provider_key)}
              disabled={testingProvider !== null}
            />
          </Tooltip>
          {canManageAI && (
            <Tooltip title="Remove">
              <Popconfirm
                title="Remove this provider?"
                description="This will delete the stored API key."
                onConfirm={() => deleteProviderMutation.mutate(record.provider_key)}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const modelColumns: ColumnsType<AIModel> = [
    {
      title: 'Model',
      key: 'model',
      render: (_: unknown, record: AIModel) => (
        <div>
          <div>
            <Text strong>{record.display_name}</Text>
            {record.is_default === 1 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>Default</Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.model_name}</Text>
        </div>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider_key',
      key: 'provider_key',
      width: 120,
      render: (key: ProviderKey) => PROVIDER_NAMES[key] || key,
    },
    {
      title: 'Max Tokens',
      dataIndex: 'max_tokens',
      key: 'max_tokens',
      width: 100,
      render: (tokens?: number) => tokens ? tokens.toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AIModel) => (
        <Space>
          <Tooltip title={record.is_starred ? 'Unstar' : 'Star'}>
            <Button
              type="text"
              size="small"
              icon={record.is_starred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => toggleStarMutation.mutate(record.id)}
            />
          </Tooltip>
          {record.is_default !== 1 && (
            <Tooltip title="Set as default">
              <Button
                type="text"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => setDefaultMutation.mutate(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const promptColumns: ColumnsType<AIPrompt> = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, record: AIPrompt) => (
        <div>
          <Text strong>{record.name}</Text>
          {record.description && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cat: string) => <Tag>{cat}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active: number) => (
        active ? <Badge status="success" text="Active" /> : <Badge status="default" text="Inactive" />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AIPrompt) => (
        <Space>
          {canManageAI && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenPromptModal(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this prompt?"
                  onConfirm={() => deletePromptMutation.mutate(record.id)}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  const taskColumns: ColumnsType<AITask> = [
    {
      title: 'Task',
      key: 'task',
      render: (_: unknown, record: AITask) => (
        <div>
          <Text strong>{record.display_name}</Text>
          {record.description && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: 'Key',
      dataIndex: 'task_key',
      key: 'task_key',
      width: 180,
      render: (key: string) => <Text code>{key}</Text>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cat: string) => <Tag>{cat}</Tag>,
    },
    {
      title: 'Usage',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 80,
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      width: 80,
      render: (enabled: boolean) => (
        enabled ? <Badge status="success" text="Enabled" /> : <Badge status="default" text="Disabled" />
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>AI Settings</Title>
        <Text type="secondary">Configure AI providers, models, prompts, and tasks</Text>
      </div>

      <Tabs
        defaultActiveKey="providers"
        items={[
          {
            key: 'providers',
            label: (
              <span>
                <KeyOutlined />
                Providers
              </span>
            ),
            children: (
              <Card
                title="API Providers"
                extra={
                  canManageAI && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenProviderModal}>
                      Add Provider
                    </Button>
                  )
                }
              >
                {providersLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : providers.length === 0 ? (
                  <Empty
                    description="No providers configured"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    {canManageAI && (
                      <Button type="primary" onClick={handleOpenProviderModal}>
                        Add Your First Provider
                      </Button>
                    )}
                  </Empty>
                ) : (
                  <Table
                    columns={providerColumns}
                    dataSource={providers}
                    rowKey="provider_key"
                    pagination={false}
                    size="small"
                  />
                )}

                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Supported providers: OpenAI, OpenRouter, Anthropic, Cloudflare AI.
                    API keys are encrypted before storage.
                  </Text>
                </div>
              </Card>
            ),
          },
          {
            key: 'models',
            label: (
              <span>
                <RobotOutlined />
                Models
              </span>
            ),
            children: (
              <Card title="Available Models">
                {modelsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : models.length === 0 ? (
                  <Empty
                    description="No models available. Add a provider to see available models."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Table
                    columns={modelColumns}
                    dataSource={models}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'prompts',
            label: (
              <span>
                <FileTextOutlined />
                Prompts
              </span>
            ),
            children: (
              <Card
                title="Prompt Templates"
                extra={
                  canManageAI && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenPromptModal()}>
                      Create Prompt
                    </Button>
                  )
                }
              >
                {promptsLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : prompts.length === 0 ? (
                  <Empty
                    description="No prompts configured"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    {canManageAI && (
                      <Button type="primary" onClick={() => handleOpenPromptModal()}>
                        Create Your First Prompt
                      </Button>
                    )}
                  </Empty>
                ) : (
                  <Table
                    columns={promptColumns}
                    dataSource={prompts}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                )}
              </Card>
            ),
          },
          {
            key: 'tasks',
            label: (
              <span>
                <ThunderboltOutlined />
                Tasks
              </span>
            ),
            children: (
              <Card title="AI Tasks">
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  Tasks are pre-configured AI operations that can be triggered from text fields
                  throughout the application. Use tasks to organize content, improve clarity,
                  extract action items, and more.
                </Paragraph>

                {tasksLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : tasks.length === 0 ? (
                  <Empty
                    description="No tasks available. Run database migrations to seed default tasks."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Table
                    columns={taskColumns}
                    dataSource={tasks}
                    rowKey="task_key"
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                )}
              </Card>
            ),
          },
        ]}
      />

      {/* Add Provider Modal */}
      <Modal
        title="Configure AI Provider"
        open={providerModalOpen}
        onOk={handleSaveProvider}
        onCancel={() => setProviderModalOpen(false)}
        confirmLoading={addProviderMutation.isPending}
        okText="Save"
      >
        <Form form={providerForm} layout="vertical">
          <Form.Item
            name="provider_key"
            label="Provider"
            rules={[{ required: true, message: 'Select a provider' }]}
          >
            <Select placeholder="Select provider">
              <Select.Option value="openai">OpenAI</Select.Option>
              <Select.Option value="openrouter">OpenRouter</Select.Option>
              <Select.Option value="anthropic">Anthropic</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="api_key"
            label="API Key"
            rules={[{ required: true, message: 'Enter your API key' }]}
            extra="Your API key will be encrypted before storage"
          >
            <Input.Password placeholder="sk-..." />
          </Form.Item>

          <Form.Item
            name="api_endpoint"
            label="Custom Endpoint (optional)"
            extra="Override the default API endpoint"
          >
            <Input placeholder="https://..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Prompt Modal */}
      <Modal
        title={editingPrompt ? 'Edit Prompt' : 'Create Prompt'}
        open={promptModalOpen}
        onOk={handleSavePrompt}
        onCancel={() => {
          setPromptModalOpen(false);
          setEditingPrompt(null);
          promptForm.resetFields();
        }}
        confirmLoading={createPromptMutation.isPending || updatePromptMutation.isPending}
        okText="Save"
        width={640}
      >
        <Form form={promptForm} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Enter a name' }]}
          >
            <Input placeholder="My Prompt" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input placeholder="What this prompt does..." />
          </Form.Item>

          <Form.Item name="category" label="Category">
            <Select placeholder="Select or type category" allowClear>
              <Select.Option value="general">General</Select.Option>
              <Select.Option value="writing">Writing</Select.Option>
              <Select.Option value="organization">Organization</Select.Option>
              <Select.Option value="analysis">Analysis</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="system_message"
            label="System Message"
            extra="Instructions for the AI model"
          >
            <TextArea rows={3} placeholder="You are a helpful assistant..." />
          </Form.Item>

          <Form.Item
            name="template"
            label="Template"
            rules={[{ required: true, message: 'Enter the prompt template' }]}
            extra="Use {{variable}} for placeholders"
          >
            <TextArea rows={6} placeholder="{{content}}" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
